import * as React from "react";
import {ReactElement, useEffect} from "react";
import {useStore, useStoreValue} from "react-context-hook";
import {getUrlVar} from "orxapi.tools.url";
import {getLocalStorageItem, setLocalStorageItem} from "../../../tools/LocalStorage";
import {getDays, getMonths, getYears, paxSelectConfig} from "../../../config/pax";
import {urlParametersConfig} from "../../../config/form";
import ErrorMessage, {ErrorCode} from "../ErrorMessage";
import BookingTravelersItem from "../BookingTravelersItem";
import BirthDateSelection from "../BirthDateSelection";
import {selectionDate, TravelItems} from "../../../typings/bookingEngine/bookingEngine";

/**
 * MainLayout
 * @constructor
 */
const BookingTravelers = (): ReactElement => {

    /**
     * TODO :
     * - add birth date for children pax in store (see 'puy du fou' as an example)
     * - add constraints check and display errors (see 'puy du fou' as an example)
     */

    const [pax, setPax] = useStore("pax");
    const nbAdults = pax.nbAdults;
    const nbChildren = pax.nbChildren;
    const months = getMonths();
    const years = getYears();
    const days = getDays(months[0].value, years[0].value);
    const [paxError, setPaxError] = useStore("paxError");
    const [minPax, setMinPax] = useStore("minPax");
    const [maxPax, setMaxPax] = useStore("maxPax");
    const selectedDepartureDate = useStoreValue("selectedDepartureDate");
    const selectedCapacity = useStoreValue("selectedCapacity");
    const disabled = paxError !== "" ? "disabled" : "";
    const localPax = Array(nbChildren).fill(1).map((value: number, paxIndex: number) => {
        return {
            day: pax.children[paxIndex]?.day || days[0],
            month: pax.children[paxIndex]?.month || months[0],
            year: pax.children[paxIndex]?.year || years[0]
        };
    });

    useEffect(() => {
        if (selectedCapacity && selectedCapacity.code) {
            const selectedMaxPax = selectedCapacity.code.split("-")[1];
            const selectedMinPax = selectedCapacity.code.split("-")[0];
            setMaxPax(selectedMaxPax);
            setMinPax(selectedMinPax);
        } else if (selectedDepartureDate) {
            const selectedMaxPax = selectedDepartureDate.value?.maxPax || paxSelectConfig.adult.options.length;
            const selectedMinPax = selectedDepartureDate.value?.minPax || 1;
            setMaxPax(selectedMaxPax);
            setMinPax(selectedMinPax);
        }
    }, [selectedDepartureDate, selectedCapacity]);

    useEffect(() => {

        if (maxPax && nbAdults + nbChildren > maxPax) {
            setPax({
                nbAdults: maxPax,
                nbChildren: 0,
                children: {}
            });
            updatePaxInfo(urlParametersConfig.adults, String(maxPax));
            updatePaxInfo(urlParametersConfig.children, String(0));
            updatePaxInfo(urlParametersConfig.birthdates, "");
        }
    }, [maxPax]);

    useEffect(() => {
        if (maxPax && minPax) {
            const totalPax = nbAdults + nbChildren;
            testPassengerConstraintsValidation(maxPax, minPax, totalPax);
        }
    }, [pax, nbAdults, nbChildren]);

    /**
     * Test passenger constraints
     */
    const testPassengerConstraintsValidation = (selectedMaxPax: any, selectedMinPax: any, totalPax: number) => {
        if (totalPax > maxPax) {
            setPaxError(ErrorCode.MAX_PAX_NUMBER);
        } else if (totalPax < minPax) {
            setPaxError(ErrorCode.MIN_PAX_NUMBER);
        } else {
            setPaxError("");
        }
    };
    const handleAddAdult = () => {
        if (nbChildren + nbAdults < maxPax) {
            pax.nbAdults = (nbAdults + 1);
            setPax(pax);
            updatePaxInfo(urlParametersConfig.adults, pax.nbAdults);
        }
    };

    const handleRemoveAdult = () => {
        if (nbAdults > 1 && nbChildren + nbAdults > minPax) {
            pax.nbAdults = (nbAdults - 1);
            setPax(pax);
            updatePaxInfo(urlParametersConfig.adults, pax.nbAdults);
        }
    };

    const handleAddChild = () => {
        if (nbChildren + nbAdults < maxPax) {
            pax.nbChildren = (nbChildren + 1);

            setPax(pax);
            updatePaxInfo(urlParametersConfig.children, pax.nbChildren);

            const childrenAge = getLocalStorageItem(urlParametersConfig.birthdates).split(",");
            const age = days[0].value + "/" + months[0].value + "/" + years[0].value;
            if (childrenAge) {
                childrenAge[pax.nbChildren - 1] = age;
                setLocalStorageItem(urlParametersConfig.birthdates, "" + childrenAge);
            }
            const queryParams = new URLSearchParams(window.location.hash);
            queryParams.set(urlParametersConfig.birthdates + "_" + (pax.nbChildren), age);
            const url = decodeURIComponent(queryParams.toString()).replace("#", "");
            history.replaceState(null, null, "#" + url);
        }
    };

    const handleRemoveChild = () => {
        if (nbChildren > 0 && nbChildren + nbAdults > minPax) {
            pax.nbChildren = (nbChildren - 1);
            if (pax.nbChildren === 0) {
                pax.children = {};
                setLocalStorageItem(urlParametersConfig.birthdates, "");
            } else {
                pax.children[nbChildren] = {};
            }
            setPax(pax);
            updatePaxInfo(urlParametersConfig.children, pax.nbChildren, pax.nbChildren, null, true);
        }
    };

    const handleDayChange = (day: string, paxIndex: number) => {
        if (!pax.children[paxIndex]) {
            pax.children[paxIndex] = {};
        }
        pax.children[paxIndex].day = {label: day, value: day};
        setPax(pax);
        const newDate = getChildBirthDate(paxIndex, day);
        updatePaxInfo(urlParametersConfig.birthdates, newDate, paxIndex, true);
    };

    const handleMonthChange = (month: string, paxIndex: number) => {
        if (!pax.children[paxIndex]) {
            pax.children[paxIndex] = {};
        }
        pax.children[paxIndex].month = {label: month, value: month};
        setPax(pax);
        const newDate = getChildBirthDate(paxIndex, null, month);
        updatePaxInfo(urlParametersConfig.birthdates, newDate, paxIndex, true);
    };

    const handleYearChange = (year: string, paxIndex: number) => {
        if (!pax.children[paxIndex]) {
            pax.children[paxIndex] = {};
        }
        pax.children[paxIndex].year = {label: year, value: year};
        setPax(pax);
        const newDate = getChildBirthDate(paxIndex, null, null, year);
        updatePaxInfo(urlParametersConfig.birthdates, newDate, paxIndex, true);
    };

    const getChildBirthDate = (paxIndex: number, replaceDay?: string, replaceMonth?: string, replaceYear?: string) => {
        const birthDateChild = getUrlVar()[urlParametersConfig.birthdates + "_" + (paxIndex + 1)];
        if (birthDateChild) {
            const birthDateChildAge = birthDateChild.split("/");
            return (replaceDay || birthDateChildAge[0]) + "/" + (replaceMonth || birthDateChildAge[1]) + "/" + (replaceYear || birthDateChildAge[2]);
        }
    };

    const updatePaxInfo = (name: string, value: any, paxIndex?: number, birthdate?: boolean, removeChild?: boolean) => {
        const queryParams = new URLSearchParams(window.location.hash);
        let valueInStore = value;
        let finalName = name;
        if (birthdate) {
            finalName = name + "_" + (paxIndex + 1);
            const childrenAge = getLocalStorageItem(name).split(",");
            if (childrenAge) {
                childrenAge[paxIndex] = value;
                valueInStore = childrenAge;
            }
        }

        if (removeChild) {
            queryParams.delete(urlParametersConfig.birthdates + "_" + (paxIndex + 1));
            const childrenAge = getLocalStorageItem(urlParametersConfig.birthdates).split(",");
            if (childrenAge) {
                childrenAge.splice(-1);
                setLocalStorageItem(urlParametersConfig.birthdates, "" + childrenAge);
            }
        }
        queryParams.set(finalName, value);
        const parameter = decodeURIComponent(queryParams.toString()).replace("#", "");
        history.replaceState(null, null, "#" + parameter);
        setLocalStorageItem(name, valueInStore);
        $("input[name='productUrl']").val(window.location.href);
    };

    return (
        <div className="cpt-booking-travelers">
            <div className="booking-travelers-content">
                <BookingTravelersItem btnLabel={TravelItems.adults} handleAdd={handleAddAdult} handleRemove={handleRemoveAdult} nbTravelers={nbAdults}/>
                <BookingTravelersItem btnLabel={TravelItems.children} handleAdd={handleAddChild} handleRemove={handleRemoveChild} nbTravelers={nbChildren}>
                    <ErrorMessage errorCode={paxError} minPax={minPax} maxPax={maxPax}/>
                    <div className="age-children-selection">
                        {
                            localPax.map((value: selectionDate, index: number) => {
                                const birthdate = value?.day && value?.month && value?.year ? `${value.day.value}-${value.month.value}-${value.year.value}` : undefined;
                                const childBirthDate: selectionDate = {
                                    day: value?.day ?? null,
                                    month: value?.month ?? null,
                                    year: value.year ?? null
                                };

                                return (
                                    <BirthDateSelection key={`children-selection-${index}`} index={index} date={childBirthDate}
                                        onDayChange={handleDayChange}
                                        onMonthChange={handleMonthChange}
                                        onYearChange={handleYearChange}>
                                        {birthdate && <input type={"hidden"} name={`traveller[${index + nbAdults}].birthDate`} value={birthdate}/>}
                                    </BirthDateSelection>
                                );
                            })
                        }
                    </div>
                </BookingTravelersItem>
                <div className="btn-wrap">
                    <span className={`elem-button elem-button--default ${disabled}`}>Appliquer</span>
                </div>
            </div>
        </div>
    );
};

export default BookingTravelers;
